const PREFIX = 'petrovich';
const DRAFT_KEY_PREFIX = `${PREFIX}:draft:`;
const DRAFT_IMAGE_KEY_PREFIX = `${PREFIX}:draft-image:`;
const DRAFT_IMAGE_NAME_KEY_PREFIX = `${PREFIX}:draft-image-name:`;
const DRAFT_IMAGES_KEY_PREFIX = `${PREFIX}:draft-images:`;
const SHOW_SYSTEM_PREFIX = `${PREFIX}:show-system:`;
const CHAT_SEARCH_KEY = `${PREFIX}:chat-search`;
const APP_SETTINGS_CACHE_KEY = `${PREFIX}:app-settings`;
// Этап 13: подсказка про удержание кнопки «Вверх» показывается один раз на устройство.
const SCROLL_UP_HINT_KEY = `${PREFIX}:hint:scroll-up`;

function hasStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function hasSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function getDraftKey(chatId) {
  return `${DRAFT_KEY_PREFIX}${chatId}`;
}

export function getDraft(chatId) {
  return hasStorage() && chatId ? window.localStorage.getItem(getDraftKey(chatId)) || '' : '';
}

export function setDraft(chatId, value) {
  if (!hasStorage() || !chatId) {
    return;
  }
  window.localStorage.setItem(getDraftKey(chatId), value ?? '');
}

export function clearDraft(chatId) {
  if (hasStorage() && chatId) {
    window.localStorage.removeItem(getDraftKey(chatId));
  }
}

export function getDraftAttachments(chatId) {
  if (!hasStorage() || !chatId) {
    return [];
  }

  const raw = window.localStorage.getItem(`${DRAFT_IMAGES_KEY_PREFIX}${chatId}`);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item) => item && typeof item.dataUrl === 'string')
          .map((item) => ({ dataUrl: item.dataUrl, name: typeof item.name === 'string' ? item.name : '' }));
      }
    } catch (_) {
      // fall through to legacy single-attachment read
    }
  }

  // Legacy single-attachment fallback (drafts created before multi-image support).
  const legacy = window.localStorage.getItem(`${DRAFT_IMAGE_KEY_PREFIX}${chatId}`);
  if (legacy) {
    return [{ dataUrl: legacy, name: window.localStorage.getItem(`${DRAFT_IMAGE_NAME_KEY_PREFIX}${chatId}`) || '' }];
  }

  return [];
}

function isQuotaError(error) {
  if (!error) {
    return false;
  }
  if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return true;
  }
  return typeof error.code === 'number' && (error.code === 22 || error.code === 1014);
}

function removeDraftAttachmentKeys(chatId) {
  try {
    window.localStorage.removeItem(`${DRAFT_IMAGES_KEY_PREFIX}${chatId}`);
    window.localStorage.removeItem(`${DRAFT_IMAGE_KEY_PREFIX}${chatId}`);
    window.localStorage.removeItem(`${DRAFT_IMAGE_NAME_KEY_PREFIX}${chatId}`);
  } catch (_) {
    // best effort cleanup
  }
}

export function setDraftAttachments(chatId, images = []) {
  if (!hasStorage() || !chatId) {
    return true;
  }

  removeDraftAttachmentKeys(chatId);
  if (!images.length) {
    return true;
  }

  try {
    const serialized = JSON.stringify(
      images.map((item) => ({ dataUrl: item.dataUrl, name: item.name || '' }))
    );
    window.localStorage.setItem(`${DRAFT_IMAGES_KEY_PREFIX}${chatId}`, serialized);
    return true;
  } catch (error) {
    removeDraftAttachmentKeys(chatId);
    if (isQuotaError(error)) {
      return false;
    }
    throw error;
  }
}

export function clearDraftAttachments(chatId) {
  setDraftAttachments(chatId, []);
}

export function getScrollUpHintSeen() {
  if (!hasStorage()) {
    return true;
  }
  return window.localStorage.getItem(SCROLL_UP_HINT_KEY) === '1';
}

export function setScrollUpHintSeen() {
  if (!hasStorage()) {
    return;
  }
  window.localStorage.setItem(SCROLL_UP_HINT_KEY, '1');
}

export function getShowSystemMessages(chatId) {
  if (!hasStorage() || !chatId) {
    return false;
  }
  return window.localStorage.getItem(`${SHOW_SYSTEM_PREFIX}${chatId}`) === '1';
}

export function setShowSystemMessages(chatId, value) {
  if (!hasStorage() || !chatId) {
    return;
  }
  if (value) {
    window.localStorage.setItem(`${SHOW_SYSTEM_PREFIX}${chatId}`, '1');
  } else {
    window.localStorage.removeItem(`${SHOW_SYSTEM_PREFIX}${chatId}`);
  }
}

export function getSessionChatSearch() {
  return hasSessionStorage() ? window.sessionStorage.getItem(CHAT_SEARCH_KEY) || '' : '';
}

export function setSessionChatSearch(value) {
  if (!hasSessionStorage()) {
    return;
  }

  if (value) {
    window.sessionStorage.setItem(CHAT_SEARCH_KEY, value);
  } else {
    window.sessionStorage.removeItem(CHAT_SEARCH_KEY);
  }
}

// Кэш нормализованных настроек приложения: тема/шрифт применяются в main.js ДО
// монтажа (анти-«вспышка тёмной темы» у пользователей светлой/системной темы).
export function getCachedAppSettings() {
  if (!hasStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(APP_SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function setCachedAppSettings(settings) {
  if (!hasStorage()) {
    return;
  }
  try {
    // Только лёгкие поля отображения (без avatarDataUrl — base64 раздул бы квоту).
    const { theme, fontScale } = settings || {};
    window.localStorage.setItem(APP_SETTINGS_CACHE_KEY, JSON.stringify({ theme, fontScale }));
  } catch (_) {
    // best effort
  }
}
